import React from "react";
import { gql, useQuery } from "@apollo/client";
import {
  Withdrawal,
  FindMyWithdrawalResponse,
  QueryFindMyWithdrawalsArgs,
} from "../../../../schemaTypes";

export const FIND_MY_WITHDRAWALS = gql`
  query FindMyWithdrawals(
    $page: Int!
    $pageSize: Int!
    $statusFilter: String
    $searchText: String
  ) {
    findMyWithdrawals(
      page: $page
      pageSize: $pageSize
      statusFilter: $statusFilter
      searchText: $searchText
    ) {
      data {
        withdrawal {
          id
          transaction {
            id
            amount
            charge
            reference
            type
            status
            currency
            transferTypes
            wallet {
              id
              balance
              createdAt
              updatedAt
            }
            recipientWallet {
              id
              balance
              currency
              owner{
                admin{
                  merchantProfile{
                    companyName
                  }
                }
              }
              createdAt
              updatedAt
            }
            offer{
              id
              title
              description
              bannerUrl
              category {
                id
                name
                imgSrc
                createdBy{
                  id
                  role
                }
                createdAt
                updatedAt
              }
              offerType
              discountType
              discountValue
              partnerVoucher
              referralUrl
              amount
              status
              usageLimitPerUser
              totalUsageLimit
              startDate
              endDate
              community{
                id
                name
                alias
              }
              redemptions
              revenueGenerated
              clickThroughs
              featured
              createdBy{
                id
                role
                admin {
                  id
                  shortId
                  firstname
                  lastname
                  email
                  phone
                  merchantProfile {
                    id
                    businessName
                    businessType
                    regCountry
                    businessRegNumber
                    taxId
                    address
                    addressAlt
                    city
                    country
                    postcode
                    companyName
                    username
                    industry {
                      id
                      name
                      createdBy{
                        id
                        role
                      }
                      createdAt
                      updatedAt
                    }
                    companyLocation
                    logoUrl
                    bannerUrl
                    description
                    commissionRate
                    createdAt
                    updatedAt
                  }
                  createdAt
                  updatedAt
                }
              }
              # njoyCode {
              #   id
              #   code
              #   redeemedAt
              #   createdAt
              #   updatedAt
              # }
              createdAt
              updatedAt
            }
            njoyCode
            partnerVoucher
            user {
              id
              # createdAt
              # updatedAt
            }
            createdAt
            updatedAt
          }
          bankPreference{
            id
            bank{
              id
              bank
              bankCode
              pictureUrl
              createdAt
              updatedAt
            }
            accountNo
            owner{
              id
              # createdAt
              # updatedAt
            }
            createdAt
            updatedAt
          }
          user {
            id
            # createdAt
            # updatedAt
          }
          createdAt
          updatedAt
        }
        totalWithdrawal
      }
      pageSize
      page
      total
      totalPages
    }
  }
`;

interface WithdrawalWithTotal {
  withdrawal: Withdrawal;
  totalWithdrawal: number;
}

interface WithFindMyWithdrawals {
  withdrawals: WithdrawalWithTotal[];
  loading: boolean;
  loadMore: () => void;
  hasMore: boolean;
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

interface WithdrawalProps {
  variables: QueryFindMyWithdrawalsArgs;
  children: (data: WithFindMyWithdrawals) => React.ReactElement | null;
}

export const FindMyWithdrawals: React.FC<WithdrawalProps> = ({ children, variables }) => {
  const { data, loading, fetchMore } = useQuery<
    { findMyWithdrawals: FindMyWithdrawalResponse },
    QueryFindMyWithdrawalsArgs
  >(
    FIND_MY_WITHDRAWALS,
    {
      variables,
      notifyOnNetworkStatusChange: true,
    }
  );

  const withdrawals = data?.findMyWithdrawals?.data || [];
  const page = data?.findMyWithdrawals?.page || variables.page || 1;
  const pageSize = data?.findMyWithdrawals?.pageSize || variables.pageSize || 10;
  const total = data?.findMyWithdrawals?.total || 0;
  const totalPages = data?.findMyWithdrawals?.totalPages || 0;
  const hasMore = page < totalPages;

  const loadMore = React.useCallback(() => {
    if (!hasMore) return;
    fetchMore({
      variables: {
        ...variables,
        page: page + 1,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult || !fetchMoreResult.findMyWithdrawals) {
          return prev;
        }
        return {
          ...fetchMoreResult,
          findMyWithdrawals: {
            ...fetchMoreResult.findMyWithdrawals,
            data: [
              ...(prev?.findMyWithdrawals?.data || []),
              ...(fetchMoreResult.findMyWithdrawals.data || [])
            ]
          }
        };
      }
    });
  }, [fetchMore, variables, page, hasMore]);

  return children({
    withdrawals: withdrawals as WithdrawalWithTotal[],
    loading,
    loadMore,
    hasMore,
    page,
    pageSize,
    total,
    totalPages,
  });
};